import React, { useState, useEffect } from 'react';
import { deviceRequestAPI, settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DeviceRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processing, setProcessing] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    deliveryUnit: '',
    deviceType: '',
    deviceName: '',
    model: '',
    expectedDate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Settings data
  const [deliveryUnits, setDeliveryUnits] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  // Status update dialog
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    requestId: null,
    status: '',
    remarks: ''
  });

  const isManager = user?.role === 'device_manager' || user?.role === 'super_admin';

  useEffect(() => {
    if (user) {
      loadRequests();
      loadSettings();
      // Set user's delivery unit as default if available
      if (user?.deliveryUnit) {
        setFormData(prev => ({ ...prev, deliveryUnit: user.deliveryUnit }));
      }
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      const [duRes, dtRes] = await Promise.all([
        settingsAPI.getDeliveryUnits(),
        settingsAPI.getDeviceTypes()
      ]);
      console.log('Loaded delivery units:', duRes.data);
      console.log('Loaded device types:', dtRes.data);
      setDeliveryUnits(duRes.data || []);
      setDeviceTypes(dtRes.data || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      // Managers see all requests in their DU, employees see their own
      const response = isManager 
        ? await deviceRequestAPI.getAll() 
        : await deviceRequestAPI.getMyRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading device requests:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to load device requests' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    // For employees, use their delivery unit; for managers use form selection
    const deliveryUnit = user?.role === 'employee' ? user?.deliveryUnit : formData.deliveryUnit;
    
    // Validation
    if (!deliveryUnit || !formData.deviceType || !formData.deviceName || !formData.expectedDate) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await deviceRequestAPI.create({
        ...formData,
        deliveryUnit // Ensure we use the correct delivery unit
      });
      setMessage({ type: 'success', text: 'Device request submitted successfully! Managers have been notified.' });
      setShowRequestForm(false);
      setFormData({
        deliveryUnit: user?.deliveryUnit || '',
        deviceType: '',
        deviceName: '',
        model: '',
        expectedDate: ''
      });
      loadRequests();
    } catch (error) {
      console.error('Error submitting device request:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to submit device request' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openStatusDialog = (requestId, newStatus) => {
    setStatusDialog({
      open: true,
      requestId,
      status: newStatus,
      remarks: ''
    });
  };

  const closeStatusDialog = () => {
    setStatusDialog({
      open: false,
      requestId: null,
      status: '',
      remarks: ''
    });
  };

  const handleUpdateStatus = async () => {
    if (!statusDialog.requestId || !statusDialog.status) return;

    setProcessing(statusDialog.requestId);
    setMessage({ type: '', text: '' });

    try {
      await deviceRequestAPI.updateStatus(statusDialog.requestId, {
        status: statusDialog.status,
        remarks: statusDialog.remarks
      });
      setMessage({ 
        type: 'success', 
        text: `Request ${statusDialog.status === 'resolved' ? 'resolved' : 'cancelled'} successfully.` 
      });
      closeStatusDialog();
      loadRequests();
    } catch (error) {
      console.error('Error updating request status:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to update request status' 
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    setProcessing(requestId);
    setMessage({ type: '', text: '' });

    try {
      await deviceRequestAPI.delete(requestId);
      setMessage({ type: 'success', text: 'Request deleted successfully.' });
      loadRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to delete request' 
      });
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'resolved':
        return '#10b981';
      case 'cancelled':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'fa-clock';
      case 'resolved':
        return 'fa-check-circle';
      case 'cancelled':
        return 'fa-times-circle';
      default:
        return 'fa-question-circle';
    }
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Device Requests</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Device Requests</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowRequestForm(!showRequestForm)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <i className={`fas fa-${showRequestForm ? 'times' : 'plus'}`}></i>
            {showRequestForm ? 'Cancel' : 'New Request'}
          </button>
        </div>
        <p style={{ color: 'var(--light-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
          {isManager 
            ? 'Manage device requests from your delivery unit' 
            : 'Request a device and track your requests'}
        </p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      {/* Request Form */}
      {showRequestForm && (
        <div style={{
          backgroundColor: '#f9fafb',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.125rem' }}>
            <i className="fas fa-file-alt" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
            New Device Request
          </h3>
          
          {(deviceTypes.length === 0 || (user?.role !== 'employee' && deliveryUnits.length === 0)) && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              padding: '0.75rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#92400e'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
              Loading form options... If this persists, please refresh the page or contact your administrator.
            </div>
          )}
          
          <form onSubmit={handleSubmitRequest}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Delivery Unit <span style={{ color: '#dc2626' }}>*</span>
                </label>
                {user?.role === 'employee' ? (
                  <input
                    type="text"
                    value={user?.deliveryUnit || 'Not Assigned'}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      cursor: 'not-allowed'
                    }}
                  />
                ) : (
                  <select
                    value={formData.deliveryUnit}
                    onChange={(e) => handleFormChange('deliveryUnit', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                    required
                    disabled={deliveryUnits.length === 0}
                  >
                    <option value="">{deliveryUnits.length === 0 ? 'Loading delivery units...' : 'Select Delivery Unit'}</option>
                    {deliveryUnits.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Device Type <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={formData.deviceType}
                  onChange={(e) => handleFormChange('deviceType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                  required
                  disabled={deviceTypes.length === 0}
                >
                  <option value="">{deviceTypes.length === 0 ? 'Loading device types...' : 'Select Device Type'}</option>
                  {deviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Device Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.deviceName}
                  onChange={(e) => handleFormChange('deviceName', e.target.value)}
                  placeholder="e.g., iPhone 14 Pro"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Model <span style={{ color: '#6b7280', fontWeight: 400 }}>(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => handleFormChange('model', e.target.value)}
                  placeholder="e.g., A2894"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem' }}>
                  Expected Date <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.expectedDate}
                  onChange={(e) => handleFormChange('expectedDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowRequestForm(false)}
                style={{ padding: '0.625rem 1.25rem' }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ padding: '0.625rem 1.25rem' }}
                disabled={submitting}
              >
                {submitting ? (
                  <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Submit Request</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Requests List */}
      {requests.length === 0 ? (
        <div style={{ 
          padding: '3rem', 
          textAlign: 'center', 
          color: 'var(--light-text-secondary)' 
        }}>
          <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}></i>
          <p style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>No device requests</p>
          <p style={{ fontSize: '0.875rem' }}>
            {isManager 
              ? 'No device requests in your delivery unit yet' 
              : 'Click "New Request" to submit your first device request'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {requests.map((request) => (
            <div 
              key={request._id} 
              style={{
                border: '1px solid #e0e4e7',
                borderLeft: `4px solid ${getStatusColor(request.status)}`,
                borderRadius: '8px',
                padding: '1.25rem',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{
                      padding: '0.5rem',
                      backgroundColor: getStatusColor(request.status) + '20',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className={`fas ${getStatusIcon(request.status)}`} 
                         style={{ color: getStatusColor(request.status), fontSize: '1.25rem' }}></i>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--light-text)' }}>
                        {request.deviceName}
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--light-text-secondary)' }}>
                        {request.deviceType}
                        {request.model && ` • ${request.model}`}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.35rem 0.75rem',
                      backgroundColor: '#e0e7ff',
                      borderRadius: '12px'
                    }}>
                      <i className="fas fa-building" style={{ color: '#667eea', fontSize: '0.75rem' }}></i>
                      <span style={{ fontSize: '0.8125rem', color: '#667eea', fontWeight: 600 }}>
                        {request.deliveryUnit}
                      </span>
                    </div>

                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.35rem 0.75rem',
                      backgroundColor: '#fef3c7',
                      borderRadius: '12px'
                    }}>
                      <i className="fas fa-calendar" style={{ color: '#f59e0b', fontSize: '0.75rem' }}></i>
                      <span style={{ fontSize: '0.8125rem', color: '#92400e', fontWeight: 600 }}>
                        Expected: {formatDate(request.expectedDate)}
                      </span>
                    </div>

                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      padding: '0.35rem 0.75rem',
                      backgroundColor: getStatusColor(request.status) + '20',
                      borderRadius: '12px'
                    }}>
                      <i className={`fas ${getStatusIcon(request.status)}`} 
                         style={{ color: getStatusColor(request.status), fontSize: '0.75rem' }}></i>
                      <span style={{ 
                        fontSize: '0.8125rem', 
                        color: getStatusColor(request.status), 
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {request.status}
                      </span>
                    </div>
                  </div>

                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: 'var(--light-text-secondary)',
                    marginBottom: '0.5rem'
                  }}>
                    <i className="fas fa-user" style={{ marginRight: '0.5rem' }}></i>
                    Requested by: <strong>{request.requestedByName || request.requestedBy}</strong>
                  </div>

                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--light-text-secondary)'
                  }}>
                    <i className="fas fa-clock" style={{ marginRight: '0.5rem' }}></i>
                    Submitted on {formatDateTime(request.createdAt)}
                  </div>

                  {request.remarks && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 600, 
                        color: '#374151',
                        marginBottom: '0.25rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Remarks
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                        {request.remarks}
                      </div>
                      {request.resolvedBy && (
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: '#6b7280',
                          marginTop: '0.5rem'
                        }}>
                          By {request.resolvedBy} on {formatDateTime(request.resolvedAt)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem',
                  marginLeft: '1rem'
                }}>
                  {isManager && request.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={() => openStatusDialog(request._id, 'resolved')}
                        disabled={processing === request._id}
                        style={{ 
                          padding: '0.625rem 1rem',
                          fontSize: '0.875rem',
                          minWidth: '120px'
                        }}
                      >
                        <i className="fas fa-check"></i> Resolve
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => openStatusDialog(request._id, 'cancelled')}
                        disabled={processing === request._id}
                        style={{ 
                          padding: '0.625rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#dc2626',
                          borderColor: '#dc2626',
                          minWidth: '120px'
                        }}
                      >
                        <i className="fas fa-times"></i> Cancel
                      </button>
                    </>
                  )}
                  
                  {((isManager && request.status !== 'pending') || 
                    (!isManager && request.status === 'pending' && request.requestedBy === user?.email)) && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDeleteRequest(request._id)}
                      disabled={processing === request._id}
                      style={{ 
                        padding: '0.625rem 1rem',
                        fontSize: '0.875rem',
                        minWidth: '120px'
                      }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Update Dialog */}
      {statusDialog.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
              {statusDialog.status === 'resolved' ? 'Resolve Request' : 'Cancel Request'}
            </h3>
            <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
              Add remarks (optional):
            </p>
            <textarea
              value={statusDialog.remarks}
              onChange={(e) => setStatusDialog(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Enter any remarks or notes..."
              rows="4"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                resize: 'vertical',
                marginBottom: '1rem'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={closeStatusDialog}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateStatus}
                disabled={processing}
                style={{
                  backgroundColor: statusDialog.status === 'cancelled' ? '#dc2626' : undefined
                }}
              >
                {processing ? (
                  <><i className="fas fa-spinner fa-spin"></i> Updating...</>
                ) : (
                  <><i className="fas fa-check"></i> Confirm</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceRequests;
